import 'reflect-metadata';
import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { MantineProvider, Center, Loader } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { RouterProvider } from 'react-router-dom';
import { initializeContainer } from './container/CompositionRoot';
import { ThemeContextProvider, useThemeContext } from './design/ThemeContext';
import { operationsTheme, standardTheme, themeVariablesResolver } from './design/AppTheme';
import { router } from './routes/AppRoutes';
import { AuthProvider, useAuth } from './features/auth/AuthContext';
import { TenantLandingPage } from './features/auth/TenantLandingPage';
import { fetchRuntimeConfig } from './runtime-config/RuntimeConfigLoader';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import './design/global.css';

const runtimeConfig = await fetchRuntimeConfig();
initializeContainer(runtimeConfig);

function AuthenticatedShell({ onUnauthenticated }: { onUnauthenticated: () => void }): React.ReactElement {
    const { isLoading, isAuthenticated } = useAuth();

    useEffect(() => {
        if (!isLoading && !isAuthenticated) onUnauthenticated();
    }, [isLoading, isAuthenticated, onUnauthenticated]);

    if (isLoading || !isAuthenticated) {
        return (
            <Center h="100vh">
                <Loader size="lg" />
            </Center>
        );
    }

    return <RouterProvider router={router} />;
}

function AppWithTheme(): React.ReactElement {
    const { mode, style } = useThemeContext();
    const theme = style === 'operations' ? operationsTheme : standardTheme;
    const [authNonce, setAuthNonce] = useState(0);
    const [showLogin, setShowLogin] = useState(false);

    function handleAuthenticated(): void {
        setShowLogin(false);
        setAuthNonce((n) => n + 1);
    }

    // Surfaces that bypass the tenant login gate: the operator console (its own
    // realm login) and the pre-login self-service auth pages (reset / set-password /
    // verify-email, opened from an emailed link while logged out). Render the router
    // directly so these public routes resolve.
    const publicPrefixes = ['/operator', '/reset-password', '/set-password', '/verify-email'];
    const isPublicSurface = publicPrefixes.some((prefix) => window.location.pathname.startsWith(prefix));

    return (
        <MantineProvider theme={theme} cssVariablesResolver={themeVariablesResolver} forceColorScheme={mode}>
            <Notifications position="top-right" />
            {isPublicSurface ?
                <AuthProvider>
                    <RouterProvider router={router} />
                </AuthProvider>
            : showLogin ?
                <TenantLandingPage onAuthenticated={handleAuthenticated} />
            :   <AuthProvider key={authNonce}>
                    <AuthenticatedShell onUnauthenticated={() => setShowLogin(true)} />
                </AuthProvider>
            }
        </MantineProvider>
    );
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

createRoot(rootElement).render(
    <StrictMode>
        <ThemeContextProvider>
            <AppWithTheme />
        </ThemeContextProvider>
    </StrictMode>,
);
